import { useEffect, useState } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { ACTIVATION, ASSESSMENT, CONSULT, STATUS_CLS, Pill, ago, fmtDate } from './shared';

const inp = 'w-full h-9 rounded-lg border border-slate-200 px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400';
const ACTION_LABEL = { updated: 'updated', note: 'added a note', whatsapp_sent: 'confirmed WhatsApp sent', whatsapp_skipped: 'skipped WhatsApp', whatsapp_failed: 'marked WhatsApp failed' };

const Activity = ({ items }) => (
  <ol className="space-y-2" data-testid="drawer-activity-log">
    {items.length === 0 && <li className="text-xs text-slate-400">No activity yet.</li>}
    {items.map((a) => (
      <li key={a.id} className="text-xs border-l-2 border-teal-200 pl-3">
        <span className="font-semibold text-slate-700">{a.by}</span> {ACTION_LABEL[a.action] || a.action}
        {a.field && <> · {a.field.replace(/_/g, ' ')}: <span className="text-slate-400 line-through">{a.old || '—'}</span> → {a.new || '—'}</>}
        {a.template && <> · template: {a.template}</>}
        {a.text && <p className="text-slate-600 mt-0.5 whitespace-pre-wrap">{a.text}</p>}
        <p className="text-slate-400">{new Date(a.at).toLocaleString('en-IN')}</p>
      </li>
    ))}
  </ol>
);

const Field = ({ label, children }) => <label className="block"><span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</span><div className="mt-1">{children}</div></label>;

export const StudentDrawer = ({ id, api, statuses, facets, canEdit, onClose, onSend, onChanged }) => {
  const [s, setS] = useState(null);
  const [form, setForm] = useState({});
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = (data) => { setS(data); setForm({ status: data.status, assigned_to: data.assigned_to, next_action: data.next_action_set, next_followup_at: data.next_followup_at, do_not_contact: data.do_not_contact, referral_code: data.referral_code }); };
  useEffect(() => {
    let live = true;
    if (id) api(`/students/${id}`).then((d) => live && d && load(d));
    return () => { live = false; };
  }, [id, api]);

  const save = async () => {
    setSaving(true); setErr('');
    const orig = { status: s.status, assigned_to: s.assigned_to, next_action: s.next_action_set, next_followup_at: s.next_followup_at, do_not_contact: s.do_not_contact, referral_code: s.referral_code };
    const changed = Object.fromEntries(Object.entries(form).filter(([k, v]) => v !== orig[k]));
    const data = await api(`/students/${id}`, { method: 'PATCH', body: JSON.stringify({ ...changed, note }) }, true);
    setSaving(false);
    if (data.error) setErr(data.error);
    else { load(data); setNote(''); onChanged(); }
  };

  if (!id) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="student-drawer">
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} />
      <aside className="relative w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl p-6 space-y-5">
        <button type="button" onClick={onClose} className="absolute right-4 top-4 text-slate-400 hover:text-slate-700" data-testid="drawer-close-btn"><X className="w-5 h-5" /></button>
        {!s ? <p className="text-sm text-slate-400">Loading…</p> : (<>
          <div>
            <h3 className="font-outfit text-xl font-bold text-[#0b1f4d]" data-testid="drawer-student-name">{s.full_name}</h3>
            <p className="text-sm text-slate-600">{s.email} · {s.phone || 'no mobile'}</p>
            <p className="text-xs text-slate-500 mt-1">Registered {fmtDate(s.joined_at)} · {s.source}</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <Pill cls={ASSESSMENT[s.assessment].cls}>{ASSESSMENT[s.assessment].label}</Pill>
              <Pill cls={STATUS_CLS[s.status]}>{s.status}</Pill>
              <Pill cls={s.whatsapp_consent ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'}>{s.whatsapp_consent ? 'WhatsApp opted in' : 'No WhatsApp opt-in recorded'}</Pill>
              {CONSULT[s.consultation] && <Pill cls={CONSULT[s.consultation][1]}>Clarity Session: {CONSULT[s.consultation][0]}</Pill>}
              {s.do_not_contact && <Pill cls="bg-red-100 text-red-700">Do not contact</Pill>}
            </div>
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            {[['Referred by', s.referred_by], ['Activation', ACTIVATION[s.activation]?.[0]], ['College', s.college], ['Course', s.course], ['City', s.city], ['Situation', s.current_status], ['Career interest', s.career_interest], ['Goal', s.career_goal], ['Challenge', s.career_challenge], ['Readiness focus', s.priority_area], ['Last WhatsApp', ago(s.last_contact_at)]].map(([k, v]) => (
              <div key={k}><dt className="text-slate-400">{k}</dt><dd className="text-slate-700 font-medium">{v || '—'}</dd></div>
            ))}
          </dl>
          {s.scores && <div className="flex gap-2 text-[11px]" data-testid="drawer-scores">{Object.entries(s.scores).map(([k, v]) => <span key={k} className="bg-slate-50 rounded-lg px-2 py-1">{k.replace(/_/g, ' ')}: <b>{v}/12</b></span>)}</div>}
          {canEdit && s.can_whatsapp && (
            <button type="button" onClick={() => onSend([s.id])} data-testid="drawer-send-wa-btn" className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white font-semibold py-2.5">
              <MessageCircle className="w-4 h-4" /> Send WhatsApp
            </button>
          )}
          <div className="space-y-3 border-t border-slate-100 pt-4">
            <Field label="Follow-up status">
              <select disabled={!canEdit} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inp} data-testid="drawer-status-select">
                {statuses.map((x) => <option key={x}>{x}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Assigned to">
                <select disabled={!canEdit} value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} className={inp} data-testid="drawer-assigned-input">
                  <option value="">Unassigned</option>
                  {[...new Set([...(facets?.team || []), form.assigned_to].filter(Boolean))].map((n) => <option key={n}>{n}</option>)}
                </select>
              </Field>
              <Field label="Next follow-up"><input disabled={!canEdit} type="date" value={form.next_followup_at} onChange={(e) => setForm({ ...form, next_followup_at: e.target.value })} className={inp} data-testid="drawer-next-followup-input" /></Field>
            </div>
            <Field label="Next action"><input disabled={!canEdit} value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })} className={inp} placeholder={s.next_action} data-testid="drawer-next-action-input" /></Field>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" disabled={!canEdit} checked={!!form.do_not_contact} onChange={(e) => setForm({ ...form, do_not_contact: e.target.checked })} className="accent-red-600 w-4 h-4" data-testid="drawer-dnc-toggle" />
              Do not contact (hides WhatsApp actions)
            </label>
            {canEdit && (
              <Field label="Referred by (manual, logged in history)">
                <select value={form.referral_code || ''} onChange={(e) => setForm({ ...form, referral_code: e.target.value })} className={inp} data-testid="drawer-referral-select">
                  <option value="">Direct / no referral</option>
                  {(facets?.referrals || []).map(([c, l]) => <option key={c} value={c}>{l}</option>)}
                </select>
              </Field>
            )}
            {canEdit && <Field label="Add internal note"><textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className={`${inp} h-auto py-2`} data-testid="drawer-note-input" /></Field>}
            {err && <p className="text-xs text-red-600" data-testid="drawer-save-error">{err}</p>}
            {canEdit && <button type="button" onClick={save} disabled={saving} className="w-full rounded-full bg-[#0b1f4d] text-white font-semibold py-2.5 disabled:opacity-50" data-testid="drawer-save-btn">{saving ? 'Saving…' : 'Save changes'}</button>}
          </div>
          {s.notes?.length > 0 && <div><h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Notes</h4>{s.notes.slice().reverse().map((n) => <p key={n.at} className="text-xs bg-amber-50 rounded-lg p-2 mb-1.5 whitespace-pre-wrap">{n.text}<span className="block text-slate-400 mt-0.5">{n.by} · {new Date(n.at).toLocaleString('en-IN')}</span></p>)}</div>}
          <div><h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Activity log</h4><Activity items={s.activity} /></div>
        </>)}
      </aside>
    </div>
  );
};
